<div class="container-sm">
    <h1 class="m-3 display-6 vm-container-header"><i class="bi bi-file-earmark-spreadsheet me-2"></i> Extract Config</h1>

    <div class="pb-5">
        <div class="bg-body-light rounded-3 shadow">
            <div class="m-2 pb-1">
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-2">
                    <form autocomplete="off">
                        <div class="row align-items-center">
                            <app-table-search [(value)]="this.service.searchTerm" />
                        </div>
                    </form>

                    <div class="btn-group">
                        <button class="btn btn-outline-primary" (click)="onAdd()">
                            <i class="bi bi-plus-circle me-1"></i> Add Extract
                        </button>
                        <button class="btn btn-outline-primary" (click)="onPreview()">
                            <i class="bi bi-eye me-1"></i> Preview File
                        </button>
                        <button class="btn btn-primary" (click)="onPublish()">
                            <i class="bi bi-upload me-1"></i> Publish
                        </button>
                    </div>
                </div>

                @if ((this.total$ | async) ?? 0) {
                    <table class="table table-striped table-hover">
                        <caption>{{ this.total$ | async }} Total (enabled rows are published)</caption>

                        <thead>
                        <tr>
                            <th scope="col" sortable="extractKey" (sort)="this.onSort($event)">Extract Key</th>
                            <th scope="col" sortable="type" (sort)="this.onSort($event)">Type</th>
                            <th scope="col" sortable="name" (sort)="this.onSort($event)">Name</th>
                            <th scope="col" sortable="customerCode" (sort)="this.onSort($event)">Customer</th>
                            <th scope="col" class="text-center" sortable="isEnabled" (sort)="this.onSort($event)">
                                Enabled
                            </th>
                            <th scope="col" sortable="updatedAt" (sort)="this.onSort($event)">Last Modified</th>
                            <th scope="col" class="text-center">Actions</th>
                        </tr>
                        </thead>

                        <tbody class="table-group-divider">
                            @for (element of this.entries$ | async; track element.id) {
                                <tr>
                                    <td class="align-middle text-ellipsis" title="{{ element.extractKey }}"
                                        style="max-width: 18em;">
                                        {{ element.extractKey }}
                                    </td>
                                    <td class="align-middle">
                                        <span class="badge text-bg-secondary fw-normal">{{ element.type }}</span>
                                    </td>
                                    <td class="align-middle text-ellipsis" title="{{ element.name }}"
                                        style="max-width: 16em;">{{ element.name }}</td>
                                    <td class="align-middle">{{ element.customerCode }}</td>
                                    <td class="align-middle text-center">
                                        @if (element.isEnabled) {
                                            <span class="badge text-bg-success"><i class="bi bi-check-lg"></i></span>
                                        } @else {
                                            <span class="badge text-bg-danger text-white"><i class="bi bi-slash-circle"></i></span>
                                        }
                                    </td>
                                    <td class="align-middle">{{ this.longFormatDate(element.updatedAt) }}</td>
                                    <td class="align-middle text-center">
                                        <div class="btn-group">
                                            <i class="btn btn-sm btn-outline-primary bi bi-pencil"
                                               title="Edit this extract"
                                               (click)="onEdit(element.id)"></i>
                                            <i class="btn btn-sm btn-outline-danger bi bi-trash"
                                               title="Delete this extract"
                                               (click)="onDelete(element)"></i>
                                        </div>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>

                    <app-table-pager [total]="(this.total$ | async)!" [(page)]="this.service.page" [(pageSize)]="this.service.pageSize" />
                }

                @if (((this.total$ | async) ?? 0) == 0 && !(this.globalService.waitOverlayIsVisible$ | async)) {
                    <div class="card text-center bg-warning-subtle border border-warning rounded p-3 m-5">
                        <div class="card-body">
                            <i class="bi bi-exclamation-triangle-fill me-2"></i>No extracts configured yet.
                            Click "Add Extract" to create one.
                        </div>
                    </div>
                }
            </div>
        </div>

        <!-- Add / Edit a single extract row -->
        <ng-template #editModal>
            <div class="modal-header">
                <h1 class="modal-title fs-5" id="editModalLabel">
                    {{ isEditing ? "Edit Extract" : "Add Extract" }}
                </h1>
                <button (click)="closeEditModal()" aria-label="Close" class="btn-close" type="button"></button>
            </div>

            <form [formGroup]="editForm" autocomplete="off">
                <div class="modal-body">
                    @if ((loadingDetails$ | async)) {
                        <div class="text-center p-3">
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-2">Loading details...</p>
                        </div>
                    }

                    @if (!(loadingDetails$ | async)) {
                        <div class="form-floating mb-3">
                            <input type="text" class="form-control" id="extract_key" formControlName="extract_key"
                                   placeholder="Extract key">
                            <label for="extract_key">Extract Key
                                <span class="badge text-bg-secondary fw-normal">unique identifier</span></label>
                        </div>
                        <div class="form-floating mb-3">
                            <select class="form-select" id="type" formControlName="type">
                                <option value="">Select a type</option>
                                @for (t of extractTypes; track t) {
                                    <option [value]="t">{{ t }}</option>
                                }
                            </select>
                            <label for="type">Type</label>
                        </div>
                        <div class="form-floating mb-3">
                            <input type="text" class="form-control" id="name" formControlName="name"
                                   placeholder="Extract name">
                            <label for="name">Name</label>
                        </div>
                        <div class="form-floating mb-3">
                            <textarea class="form-control" id="sql" formControlName="sql"
                                      placeholder="SQL" style="height: 10rem;"></textarea>
                            <label for="sql">SQL</label>
                        </div>
                        <div class="form-floating mb-3">
                            <input type="text" class="form-control" id="customer_code" formControlName="customer_code"
                                   placeholder="Customer Code">
                            <label for="customer_code">Customer Code</label>
                        </div>
                        <div class="form-floating mb-3">
                            <input type="text" class="form-control" id="field_separator"
                                   formControlName="field_separator" placeholder="Field Separator">
                            <label for="field_separator">Field Separator</label>
                        </div>

                        @if (editForm.controls.type.value === EXTRACT_TYPE_SFTP) {
                            <div class="form-floating mb-3">
                                <select class="form-select" id="connector_id" formControlName="connector_id">
                                    <option value="">Select a connector</option>
                                    @for (connector of availableConnectors; track connector.connectorId) {
                                        <option [value]="connector.connectorId">{{ connector.name }}</option>
                                    }
                                </select>
                                <label for="connector_id">Connector ID</label>
                            </div>
                            <div class="form-floating mb-3">
                                <input type="text" class="form-control" id="remote_directory_path"
                                       formControlName="remote_directory_path" placeholder="Remote Directory Path">
                                <label for="remote_directory_path">Remote Directory Path</label>
                            </div>
                        }

                        @if (editForm.controls.type.value === EXTRACT_TYPE_S3) {
                            <div class="border rounded p-3 mb-3">
                                <h6 class="mb-3">S3 Config</h6>
                                <div class="form-floating mb-3">
                                    <input type="text" class="form-control" id="remote_bucket"
                                           formControlName="remote_bucket" placeholder="Remote Bucket">
                                    <label for="remote_bucket">Remote Bucket</label>
                                </div>
                                <div class="form-floating mb-3">
                                    <input type="text" class="form-control" id="destination_account_id"
                                           formControlName="destination_account_id" placeholder="Destination Account ID">
                                    <label for="destination_account_id">Destination Account ID</label>
                                </div>
                                <div class="form-floating mb-0">
                                    <input type="text" class="form-control" id="destination_iam_role"
                                           formControlName="destination_iam_role" placeholder="Destination IAM Role">
                                    <label for="destination_iam_role">Destination IAM Role</label>
                                </div>
                            </div>
                        }

                        <div class="form-check form-switch">
                            <input class="form-check-input" type="checkbox" role="switch" id="is_enabled"
                                   formControlName="is_enabled">
                            <label class="form-check-label" for="is_enabled">
                                Enabled <span class="text-muted">(included in the published file)</span>
                            </label>
                        </div>
                    }
                </div>

                <div class="modal-footer">
                    <button (click)="this.onSave()" [disabled]="savingData || editForm.invalid"
                            class="btn btn-primary" title="Save this extract">
                        <span [ngClass]="{'spinner-border spinner-border-sm me-1': savingData}"></span>
                        {{ isEditing ? "Save Changes" : "Create" }}
                    </button>
                    <button (click)="closeEditModal()" class="btn btn-secondary" type="button">Close</button>
                </div>
            </form>
        </ng-template>

        <!-- Preview the stitched extracts_cfg.json (enabled rows only) without publishing -->
        <ng-template #previewModal>
            <div class="modal-header">
                <h1 class="modal-title fs-5" id="previewModalLabel">
                    Preview — extracts_cfg.json
                </h1>
                <button (click)="closePreviewModal()" aria-label="Close" class="btn-close" type="button"></button>
            </div>
            <div class="modal-body">
                @if ((loadingDetails$ | async)) {
                    <div class="text-center p-3">
                        <div class="spinner-border" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">Stitching preview...</p>
                    </div>
                }
                @if (!(loadingDetails$ | async)) {
                    <p class="text-muted">{{ previewCount }} enabled extract(s) would be published.</p>
                    <pre class="extract-preview border rounded p-2">{{ previewJson$ | async }}</pre>
                }
            </div>
            <div class="modal-footer">
                <button (click)="closePreviewModal()" class="btn btn-secondary" type="button">Close</button>
            </div>
        </ng-template>
    </div>

    <app-modal-confirm #confirmationModal></app-modal-confirm>
</div>
